import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft,
  Link2,
  Unlink,
  Search,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Users,
  Zap,
  User,
  AlertTriangle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserMemberLinking() {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [autoLinking, setAutoLinking] = useState(false);
  const [data, setData] = useState({
    unlinked_users: [],
    linked_users: [],
    available_members: [],
    total_users: 0,
    total_members: 0,
    linked_count: 0,
    unlinked_count: 0,
    available_member_count: 0,
  });
  const [searchUser, setSearchUser] = useState("");
  const [searchMember, setSearchMember] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [autoLinkResults, setAutoLinkResults] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/user-member-links`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load user-member links");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoLink = async () => {
    try {
      setAutoLinking(true);
      const res = await axios.post(
        `${API}/admin/user-member-links/auto`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutoLinkResults(res.data);
      toast.success(`Auto-linked ${res.data.linked_count} users`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto-link failed");
    } finally {
      setAutoLinking(false);
    }
  };

  const handleLink = async () => {
    if (!selectedUser || !selectedMember) {
      toast.error("Pick a user and a member first");
      return;
    }
    try {
      await axios.put(
        `${API}/users/${selectedUser.id}`,
        { member_id: selectedMember.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Linked ${selectedUser.username} → ${selectedMember.handle}`);
      setSelectedUser(null);
      setSelectedMember(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link");
    }
  };

  const handleUnlink = async (user) => {
    if (!confirm(`Unlink ${user.username} from ${user.linked_member?.handle || "their member"}?`)) {
      return;
    }
    try {
      await axios.put(
        `${API}/users/${user.id}`,
        { member_id: "" },  // empty string = unlink (server endpoint contract)
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Unlinked ${user.username}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unlink");
    }
  };

  const filteredUnlinkedUsers = useMemo(() => {
    return (data.unlinked_users || []).filter((u) => {
      if (!searchUser) return true;
      const q = searchUser.toLowerCase();
      return (
        u.username?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.title?.toLowerCase().includes(q) ||
        u.chapter?.toLowerCase().includes(q)
      );
    });
  }, [data.unlinked_users, searchUser]);

  const filteredAvailableMembers = useMemo(() => {
    return (data.available_members || []).filter((m) => {
      if (!searchMember) return true;
      const q = searchMember.toLowerCase();
      return (
        m.handle?.toLowerCase().includes(q) ||
        m.name?.toLowerCase().includes(q) ||
        m.chapter?.toLowerCase().includes(q) ||
        m.title?.toLowerCase().includes(q)
      );
    });
  }, [data.available_members, searchMember]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-slate-400 hover:text-white"
              data-testid="uml-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Link2 className="w-6 h-6 text-cyan-400" />
                User ↔ Member Linking
              </h1>
              <p className="text-slate-400 text-sm">
                Connect login accounts to their member profile. Unlinked users
                can&apos;t take quizzes, see their dues, or appear in financial
                attribution.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              className="border-slate-600 text-slate-300"
              data-testid="uml-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={handleAutoLink}
              disabled={autoLinking || data.unlinked_count === 0}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="uml-autolink-btn"
            >
              <Zap className={`w-4 h-4 mr-2 ${autoLinking ? "animate-pulse" : ""}`} />
              Auto-Link by Username
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Users" value={data.total_users} testid="uml-stat-users" />
          <StatCard label="Linked" value={data.linked_count} colorClass="text-green-400" testid="uml-stat-linked" />
          <StatCard label="Unlinked" value={data.unlinked_count} colorClass="text-amber-400" testid="uml-stat-unlinked" />
          <StatCard label="Available Members" value={data.available_member_count} testid="uml-stat-available" />
        </div>

        {/* Auto-link results banner */}
        {autoLinkResults && (
          <div className="bg-slate-800/50 border border-cyan-700 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Zap className="w-4 h-4 text-cyan-400" />
                Auto-Link Results
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAutoLinkResults(null)}
                className="text-slate-400"
                data-testid="uml-autolink-dismiss"
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-400 font-medium">{autoLinkResults.linked_count}</span>
                <span className="text-slate-400"> linked</span>
              </div>
              <div>
                <span className="text-yellow-400 font-medium">{autoLinkResults.skipped_count}</span>
                <span className="text-slate-400"> need manual linking</span>
              </div>
            </div>
            {autoLinkResults.skipped?.length > 0 && (
              <div className="mt-3 text-xs text-slate-400">
                <div className="font-medium text-slate-300 mb-1">Skipped:</div>
                <ul className="space-y-1 max-h-32 overflow-y-auto">
                  {autoLinkResults.skipped.slice(0, 20).map((s, i) => (
                    <li key={i}>
                      <span className="text-slate-300">{s.username}</span>
                      <span className="text-slate-500"> — {s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Manual linking — split panel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Unlinked Users */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <User className="w-5 h-5 text-amber-400" />
              Unlinked Users ({filteredUnlinkedUsers.length})
            </h2>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by username, email, title, chapter..."
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-600 text-white text-sm"
                data-testid="uml-user-search"
              />
            </div>
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {filteredUnlinkedUsers.map((u) => (
                <div
                  key={u.id}
                  onClick={() => setSelectedUser(u)}
                  data-testid={`uml-user-${u.id}`}
                  className={`p-2 rounded-lg cursor-pointer transition-colors ${
                    selectedUser?.id === u.id
                      ? "bg-cyan-600/30 border border-cyan-500"
                      : "bg-slate-900/50 hover:bg-slate-700/50 border border-transparent"
                  }`}
                >
                  <div className="text-white text-sm font-medium">{u.username}</div>
                  <div className="text-slate-500 text-xs">
                    {[u.title, u.chapter, u.email].filter(Boolean).join(" · ")}
                  </div>
                </div>
              ))}
              {filteredUnlinkedUsers.length === 0 && (
                <div className="text-slate-500 text-center py-4 text-sm">
                  {data.unlinked_count === 0 ? "🎉 All users are linked!" : "No matches"}
                </div>
              )}
            </div>
          </div>

          {/* All Members (annotated with existing links) */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Members ({filteredAvailableMembers.length})
            </h2>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by handle, name, title, chapter..."
                value={searchMember}
                onChange={(e) => setSearchMember(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-600 text-white text-sm"
                data-testid="uml-member-search"
              />
            </div>
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {filteredAvailableMembers.map((m) => (
                <div
                  key={m.id}
                  onClick={() => { if (!m.linked_to_username) setSelectedMember(m); }}
                  data-testid={`uml-member-${m.id}`}
                  className={`p-2 rounded-lg transition-colors ${
                    m.linked_to_username
                      ? "bg-slate-900/30 border border-transparent opacity-60 cursor-not-allowed"
                      : selectedMember?.id === m.id
                      ? "bg-cyan-600/30 border border-cyan-500 cursor-pointer"
                      : "bg-slate-900/50 hover:bg-slate-700/50 border border-transparent cursor-pointer"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-white text-sm font-medium">{m.handle}</div>
                    {m.linked_to_username && (
                      <span
                        className="text-xs text-green-400/80 whitespace-nowrap"
                        data-testid={`uml-member-linked-badge-${m.id}`}
                      >
                        linked to {m.linked_to_username}
                      </span>
                    )}
                  </div>
                  <div className="text-slate-500 text-xs">
                    {[m.title, m.chapter, m.name].filter(Boolean).join(" · ")}
                  </div>
                </div>
              ))}
              {filteredAvailableMembers.length === 0 && (
                <div className="text-slate-500 text-center py-4 text-sm">
                  No members match your search
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Link confirmation bar */}
        {(selectedUser || selectedMember) && (
          <div className="bg-cyan-900/30 border border-cyan-700 rounded-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-slate-400 text-xs mb-1">User</div>
                  <div className="text-white font-medium">
                    {selectedUser?.username || <span className="text-slate-500">Select...</span>}
                  </div>
                </div>
                <Link2 className="w-5 h-5 text-cyan-400" />
                <div className="text-center">
                  <div className="text-slate-400 text-xs mb-1">Member</div>
                  <div className="text-white font-medium">
                    {selectedMember?.handle || <span className="text-slate-500">Select...</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSelectedUser(null); setSelectedMember(null); }}
                  className="border-slate-600 text-slate-300"
                  data-testid="uml-link-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleLink}
                  disabled={!selectedUser || !selectedMember}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="uml-link-submit"
                >
                  <Link2 className="w-4 h-4 mr-1" />
                  Link
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Linked users (existing links) */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
            Linked Users ({data.linked_users.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.linked_users.map((u) => (
              <div
                key={u.id}
                className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 flex items-center justify-between"
                data-testid={`uml-linked-row-${u.id}`}
              >
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium truncate">
                    {u.username}
                    {u.dangling && (
                      <span className="ml-1 inline-flex items-center text-amber-400" title="Linked to a deleted member">
                        <AlertTriangle className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                  <div className="text-slate-500 text-xs truncate">
                    → {u.linked_member?.handle || <span className="text-amber-400">dangling</span>}
                    {u.linked_member?.chapter ? ` (${u.linked_member.chapter})` : ""}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleUnlink(u)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20 shrink-0 h-8 w-8"
                  data-testid={`uml-unlink-${u.id}`}
                >
                  <Unlink className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          {data.linked_users.length === 0 && (
            <div className="text-slate-500 text-center py-8">No users linked yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, colorClass = "text-white", testid }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
      <div className={`text-2xl font-bold ${colorClass}`} data-testid={testid}>
        {value}
      </div>
      <div className="text-slate-400 text-sm">{label}</div>
    </div>
  );
}
